import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  StaticLandingOriginalSection,
  StaticLandingOriginalSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingOriginalSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingOriginalMoreFetcherProps} from './StaticLandingOriginalMoreFetcherProps.interface';

export function StaticLandingOriginalMoreFetcher({
  children,
  section,
}: StaticLandingOriginalMoreFetcherProps) {
  const dispatch = useDispatch();
  const landingOriginalSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingOriginalSection,
        'groups'
      >
  );
  const {param: currentParam} = landingOriginalSection;
  const param: StaticLandingOriginalSectionQueryVariables['param'] = {
    categoryUid: currentParam?.categoryUid,
    subcategoryUid: currentParam.subcategory?.param,
    sortType: currentParam.sortType?.param,
    // 카테고리가 변경되는 경우에는 isComplete 정보가 직접 명시된 경우가 아니라면 완결작만 보기 여부 정보가 초기화돼야 합니다.
    isComplete: currentParam.isComplete,
    screenUid: currentParam.screenUid,
  };

  const {isFetching, isFetchedAfterMount, isSuccess, data, fetchNextPage} =
    useInfiniteStaticLandingOriginalSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {
          sectionId: section.id,
          param,
        },
        getNextPageParam: lastPage => {
          const section =
            lastPage.staticLandingOriginalSection as StaticLandingOriginalSection;
          return section.isEnd
            ? undefined
            : {
                param: {
                  ...param,
                  page: (section.param?.page || 0) + 1,
                },
                sectionId: section.id,
              };
        },
        initialData: landingOriginalSection
          ? {
              pages: [
                {
                  staticLandingOriginalSection:
                    landingOriginalSection as StaticLandingOriginalSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );
  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingOriginalSection as StaticLandingOriginalSection | undefined;

  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
